<template>
  <ontos-base v-bind="baseProps" :flush="flush" ref="base">
    <template v-if="loaded">
      <!-- Com `headerTo`, o cabeçalho é desenhado lá — na ficha, a linha das abas (pedido do usuário):
           os botões continuam do quadro, só o lugar muda. `defer`: o lugar é desenhado no mesmo ciclo,
           e só existe no fim dele. Sem `headerTo`, aqui mesmo, como sempre. -->
      <Teleport :to="headerTo || 'body'" :disabled="!headerTo" defer>
      <ontos-page-header :title="boardTitle" :class="{'board-header--elsewhere': !!headerTo}">
        <!-- v-if: sem ele o PageHeader veria o slot como preenchido mesmo vazio, e
             o título padrão (o nome da coleção) nunca apareceria. -->
        <template v-if="$slots.title" #title><slot name="title"/></template>
        <template v-if="$slots.tabs" #tabs><slot name="tabs"/></template>
        <template #actions>
          <ontos-check-menu v-if="fieldMenuItems.length"
                            icon="mdi-card-text-outline" :label="$tt('card-fields')"
                            :items="fieldMenuItems"
                            :model-value="selectedFields"
                            @update:model-value="onFieldsChange"/>
          <!-- A cor do hover é a mesma do lugar que o card deixa ao ser arrastado: os
               dois gestos dizem "é aqui". -->
          <ontos-check-menu v-if="laneMenuItems.length"
                            icon="mdi-view-week-outline" :label="laneDimension.name"
                            :items="laneMenuItems"
                            :model-value="visibleOf(laneDimension)"
                            :min="1"
                            hover-color="var(--ontos-kanban-highlight)"
                            @update:model-value="onLanesChange"/>
          <!-- Criar mora no "+" de cada faixa, e não aqui: ali o item já nasce na raia em que
               foi pedido, e o cabeçalho pediria a situação de novo no formulário. Um mesmo
               gesto em dois lugares, um deles pior. -->
          <!-- Só aparece quando há bloco escondido: sem dimensão posterior não há bloco vazio
               nenhum, e um botão que não muda nada é ruído. -->
          <ontos-header-action v-if="hiddenBlocks"
                               :icon="showEmptyBlocks ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                               :label="$tt('show-empty-blocks')"
                               @click="showEmptyBlocks = !showEmptyBlocks"/>
          <!-- Com uma dimensão só não há o que escolher nem o que ordenar, e os valores
               visíveis dela já estão no menu de faixas ao lado — o modal abriria com uma opção
               que não decide nada. -->
          <ontos-header-action v-if="allDimensions.length > 1"
                               icon="mdi-view-grid-outline" :label="$tt('dimensions')"
                               @click="dimensionsIsVisible = true"/>
          <!-- O Design não mora mais aqui: está na faixa do caminho e na barra do app (ver o App) -->
        </template>
        <!-- Os controles da janela, que só o host conhece — voltar ao dono, expandir —, fora do
             bloco das ações do quadro -->
        <template v-if="$slots.controls" #controls><slot name="controls"/></template>
        <template v-if="$slots.below" #below><slot name="below"/></template>
      </ontos-page-header>
      </Teleport>
      <div class="kanban-blocks">
        <!-- Um bloco por combinação das dimensões posteriores. Com uma dimensão só existe um
             bloco, sem cabeçalho, e o quadro fica idêntico ao de antes de blocos existirem. -->
        <ontos-kanban-block v-for="block of visibleBlocks" :key="block.key"
                            :lanes="block.lanes" :coordinate="block.coordinate"
                            :labels="block.labels" :periods="periods"
                            :context="context" :group="dragGroup"
                            :moved-fields="dimensions.map(dimension => dimension.key)"
                            :sliding="sliding" :can-create="offersCreate || !!addInLane"
                            :can-write="can('editor')"
                            :create-label-of="createLabelFor"
                            :count-title-of="countTitle"
                            :slide-offset="slideOffset"
                            @slide="slideOffset = $event"
                            @create="openCreate($event.coordinate, $event.lane, $event.element)"
                            @period-change="onPeriodChange($event.lane, $event.period)"
                            @drag-start="dragOrigin = originOf($event)"
                            @change="onCardDrop($event)"
                            @link="onInstanceLink($event.event, $event.context)">
            <template #default="{ instance, coordinate, lane }">
              <ontos-kanban-card class="mb-2"
                                 :instance="instance"
                                 :title="titleOf(instance)"
                                 :fields="cardFieldsOf(instance)"
                                 :state-option="stateOptionOf(instance)"
                                 :can-write="canEditItem(instance)"
                                 :can-edit="canEditItem(instance)"
                                 :can-unlink="!!unlinkItem"
                                 :can-delete="deletes && canDeleteItem(instance)"
                                 @edit="editInstance($event, instance)"
                                 @unlink="unlinkItem!(instance)"
                                 @delete="deleting = instance"
                                 :context="contextOf(instance)"
                                 :choice-domains="workspace.domains"
                                 :href="instanceHref(contextOf(instance))"
                                 :move-label="$tt('value-change', {property: laneDimension.name})"
                                 @open="onInstanceLink($event, contextOf(instance))"
                                 @pick-state="openPicker(instance, {...coordinate, [laneDimension.key]: lane.id})"/>
            </template>
        </ontos-kanban-block>

        <!-- Sem bloco para desenhar, a tela ficava em branco sem dizer por quê — o único sinal
             era o botão do olho no cabeçalho, e descobrir a causa dava trabalho. Diz QUAL dos
             dois vazios é, porque o remédio de cada um é outro: criar o primeiro item, ou
             reexibir um valor escondido. Só com dimensão: sem nenhuma o hospedeiro já está
             navegando para a lista, e a mensagem seria um piscar. -->
        <div v-if="dimensions.length && !visibleBlocks.length" class="text-body-2 text-grey pa-4">
          <template v-if="!instances.length">
            {{ $tt('instances-empty', {collection: idea.pluralName}) }}
          </template>
          <template v-else>
            {{ $tt('blocks-empty') }}
            <v-btn variant="text" size="small" color="primary" class="text-none"
                   @click="dimensionsIsVisible = true">{{ $tt('dimensions') }}</v-btn>
          </template>
        </div>
      </div>

      <!-- A JANELA da instância, aberta já criando: ao salvar ela vira a ficha do item criado, e
           o quadro atrás segue intocado. Antes era o diálogo de criar, que fechava e devolvia o
           quadro, com o item novo aparecendo só como mais um card.

           O vínculo e a releitura continuam antes da troca, no `onCreated`: sem o vínculo o
           filho novo não cairia no recorte de um relacionamento. -->
      <ontos-instance-dialog v-if="createIsVisible"
                             :creating="creating"
                             :context="context"
                             :workspace="workspace"
                             @created="onCreated"
                             @deleted="removeInstance($event)"
                             @showMessage="$emit('showMessage', $event)"
                             @showErrorMessage="$emit('showErrorMessage', $event)"
                             @closed="closeCreate()"/>

        <!-- O mesmo modal que a ficha da instância usa: a lista de situações é uma só -->
        <!-- Vazado quando a dimensão é um relacionamento: ali cada valor é uma instância, e a
             forma do ponto diz isso, como diz no chip e na lista. -->
        <!-- O `current` vem do ITEM, e não da coordenada de onde o modal foi aberto: numa
             dimensão múltipla o card está em várias raias ao mesmo tempo, e a coordenada diz só
             por qual delas se clicou. -->
        <ontos-state-dialog v-if="pickingFor" :heading="pickerHeading"
                            :field="pickingDimension.name"
                            :options="pickingDimension.values"
                            :form="formOf(pickingDimension)"
                            :current="pickingDimension.valuesOf(pickingFor)"
                            :multiple="!!pickingDimension.multiple"
                            :dimensions="dimensionChoices"
                            :dimension="pickingDimension.key"
                            @update:dimension="pickingKey = $event"
                            @pick="onPickState($event)"
                            @save="onSaveValues($event)"
                            @closed="pickingFor = null"/>

        <!-- A lixeira de um card: a MESMA janela de excluir da ficha, com o aviso de quem impede a
             exclusão — e cada impedidor abre por cima, como de lá. -->
        <ontos-delete-instance-dialog v-if="deleting"
                                      :context="contextOf(deleting)"
                                      :identifier="deleting._identifier"
                                      :idea-name="idea.name"
                                      @confirmed="onDeleteConfirmed()"
                                      @open-instance="openInstance($event)"
                                      @closed="deleting = null"
                                      @showErrorMessage="$emit('showErrorMessage', $event)"/>

        <!-- A pergunta do gesto, só quando ele altera uma dimensão múltipla. Fechar sem escolher
             é cancelar: nada vai ao servidor, e o reagrupamento recoloca o card. -->
        <ontos-ask-dialog v-if="asking" :title="$tt('move-or-add')"
                          :actions="moveActions"
                          @pick="onHowToMove($event)"
                          @closed="onHowToMove(null)">
          <p>{{ $tt('move-or-add-question', askingAbout) }}</p>
          <!-- UMA LINHA POR DIMENSÃO que muda: um arrasto entre blocos mexe na raia e no bloco
               ao mesmo tempo, e as duas podem ser múltiplas. Mostrando só a primeira, a pergunta
               diria metade do que vai acontecer.

               Os valores desenhados como o app desenha valor de raia em toda parte — o
               `LaneValue`, o mesmo do rótulo de bloco. -->
          <div v-for="move of askingMoves" :key="move.dimension.key"
               class="d-flex align-center flex-wrap ga-2 my-3">
            <span class="text-caption text-medium-emphasis">{{ move.dimension.name }}</span>
            <ontos-lane-value :value="move.from" :context="context"/>
            <v-icon size="small" class="text-medium-emphasis">mdi-arrow-right</v-icon>
            <ontos-lane-value :value="move.to" :context="context"/>
          </div>
          <p>{{ $tt('move-or-add-explain') }}</p>
        </ontos-ask-dialog>

        <ontos-kanban-dimensions-dialog v-if="dimensionsIsVisible"
                                        :idea="idea" :dimensions="allDimensions"
                                        :selected="dimensions" :visible="visible"
                                        :items-of="menuItemsOf"
                                        @save="onDimensionsSave($event)"
                                        @closed="dimensionsIsVisible = false"/>
    </template>
  </ontos-base>
</template>

<script lang="ts">
import {defineAsyncComponent} from 'vue'
import {Component, Prop, Watch, toNative} from 'vue-facing-decorator'
import Instance from "@/types/Instance";
import IdeaProperty from "@/types/IdeaProperty";
import Plain from "@/types/Plain";
import {ColoredOption, LaneOption} from "@/types/ColoredOption";
import IdeaLevelRouteComponent from "@/components/shared/IdeaLevelRouteComponent";
import KanbanCard from "@/components/live/instance/KanbanCard.vue";
import KanbanBlock from "@/components/live/instance/KanbanBlock.vue";
import StateDialog, {DimensionChoice} from "@/components/live/instance/StateDialog.vue";
import EditInstanceService from "@/services/EditInstanceService";
import {openEditing} from "@/utils/instanceLink";
import {InstanceContext} from "@/types/InstanceContext";
import {FinishedPeriod, KanbanPeriods, periodsFor} from "@/utils/finishedPeriod";
import {displayValues, hasValue, valueText} from "@/utils/propertyDisplay";
import {fieldVisible} from "@/types/Idea";
import {collectionReadConfig, handOverCollection, takeCollectionRead} from "@/utils/collectionRead";
import {previewOfOpened, RelatedRead} from "@/utils/instancePreview";
import OntosError from "@/model/OntosError";
import {saveErrorMessage} from "@/utils/saveErrors";
import {refuse} from "@/utils/refusal";
import AskDialog, {Ask} from "@/components/shared/AskDialog.vue";
import LaneValue from "@/components/live/instance/LaneValue.vue";
import {
  applyChangesLocally, asksHowToMove, Candidates, changesFor, changesForValues, Coordinate,
  Dimension, dimensionsOf, formOf, STATE_KEY,
  MoveAction,
  EMPTY_DIMENSION, coordinateValue, coordinateValues, CoordinateValue, fixedByFilter, inCoordinate,
  isDimensionProperty, LaneAdd, seedValues
} from "@/utils/kanbanDimensions";
import {
  allValues, BlockModel, blockModels, CardDrop, defaultValues, FrozenGroups, frozenGroupsOf,
  isEmptyBlock, LaneModel, storedValues, VisibleValues, withFrozenGroups,
  withValues
} from "@/utils/kanbanGrouping";
import {knownForCreate, lastCreated, offerKnown} from "@/utils/instanceCreation";
import {lanesSlide} from "@/utils/kanbanLayout";
import {Heading, instanceEdit, within} from "@/utils/heading";
import {IDENTIFIER_SEPARATOR} from "@/utils/ontosUtils";
import CheckMenu, {CheckMenuItem} from "@/components/shared/CheckMenu.vue";
import {watchSize} from "@/utils/elementSize";
import {animateFrom, cardPlaces, pulse} from "@/utils/motion";

const DEFAULT_FIELD_COUNT = 3

/**
 * O tabuleiro. Mesma folha para a página de rota e para o modal aberto de dentro de
 * uma instância, seguindo o contrato do InstanceView: contexto vem por prop e NUNCA
 * de $route, quem navega é o host.
 *
 * O `filter` é passado direto à listagem e é objeto Mongo — é assim que o modal
 * recorta o tabuleiro aos itens de um relacionamento, sem endpoint novo.
 */
@Component({emits: ['open-instance', 'no-dimensions', 'changed', 'candidates'],
  components: {
    //Assíncrono para quebrar o CICLO: a janela da instância monta a ficha, a ficha monta o
    //quadro de um relacionamento, e o quadro monta esta janela para criar. Importado de forma
    //direta, um dos módulos fecha o ciclo valendo `undefined` — e o sintoma é um modal que abre
    //com o indicador girando para sempre, porque a ficha lá dentro não existe e nem chega a
    //pedir nada. É o mesmo recurso que o InstanceInput já usa pela mesma razão.
    'ontos-instance-dialog': defineAsyncComponent(
        () => import('@/components/live/instance/ViewInstanceDialog.vue')),
    'ontos-kanban-block': KanbanBlock,
    'ontos-check-menu': CheckMenu,
    'ontos-kanban-card': KanbanCard,
    'ontos-state-dialog': StateDialog,
    'ontos-ask-dialog': AskDialog,
    'ontos-delete-instance-dialog': defineAsyncComponent(
        () => import('@/components/live/instance/DeleteInstanceDialog.vue')),
    'ontos-lane-value': LaneValue,
    'ontos-kanban-dimensions-dialog': defineAsyncComponent(
        () => import('@/components/live/instance/KanbanDimensionsDialog.vue'))
  },
})
class KanbanBoard extends IdeaLevelRouteComponent {
  //O mesmo nome da função, para o template chamar sem um método de repasse
  formOf = formOf
  // De onde vêm system/idea. Nunca ler $route aqui.
  @Prop({type: Object, required: true}) readonly context!: InstanceContext
  /**
   * O recorte do quadro, em filtro Mongo. Sem ele, a coleção inteira.
   *
   * Serve a DUAS coisas, e a segunda vale mesmo quando os itens chegam prontos: recortar a
   * listagem e declarar qual dimensão está presa a um valor só — ver o `fixedByFilter` no
   * `allDimensions`.
   */
  @Prop({type: Object, default: null}) readonly filter!: Plain | null
  /**
   * As instâncias já carregadas, quando quem hospeda o quadro as tem.
   *
   * É o caso do Kanban de um relacionamento: a ficha do dono lê com `relProperties` e
   * `depth: 2`, então cada item relacionado já vem inteiro — com as propriedades, a situação, os
   * carimbos de data e os relacionamentos DELE, que é do que as raias precisam. Listar de novo
   * seria pedir ao servidor o que já está na tela.
   *
   * Nulo é "carregue você", que é como o Kanban da coleção funciona.
   */
  @Prop({type: Array, default: null}) readonly items!: Instance[] | null
  /**
   * Os candidatos que o hospedeiro já tem, na forma crua da resposta — `relDomains` e
   * `reverseInfo`.
   *
   * Só com os itens vindos de fora, e existe por causa da TROCA DE ABA: alternar Lista ↔ Kanban
   * dentro da ficha desmonta e remonta este quadro, e sem alguém guardando entre uma montagem e
   * outra cada ida lia os candidatos de novo. Quem sobrevive à troca é o hospedeiro, então é
   * dele que o dado desce — foi este quadro que o leu e o devolveu pelo evento `candidates`.
   *
   * Nulo é "ainda não li", e aí este quadro lê e devolve. Mapa vazio é resposta legítima: a
   * coleção pode não apontar para nenhuma outra, e confundir as duas coisas é o que já fez o
   * formulário de criar buscar o nada em produção — ver o `relatedKnown`.
   */
  @Prop({type: Object, default: null}) readonly knownCandidates!: any
  /**
   * Como recarregar os itens, quando eles vêm de fora.
   *
   * Função, e não evento, porque o `reload` precisa AGUARDÁ-LA — o mesmo motivo do
   * `linkCreated`. Quem os carregou é quem sabe recarregá-los.
   */
  // eslint-disable-next-line no-unused-vars
  @Prop({type: Function, default: null}) readonly reloadItems!: (() => Promise<void>) | null
  /**
   * Onde moram as preferências deste quadro, quando ele não é o da coleção inteira.
   *
   * Vazio: a configuração é da coleção. Preenchido: é deste recorte — hoje, de um
   * relacionamento (ver `relationshipScope`). Sem isso, o quadro das tarefas de um projeto e a
   * página da coleção Tarefas gravariam dimensões, raias, campos e prazos na MESMA chave, e
   * cada um desfaria a escolha do outro.
   */
  @Prop({type: String, default: ''}) readonly scope!: string
  /** Valores já preenchidos ao criar — o vínculo com o dono, no caso do modal. */
  @Prop({type: Object, default: null}) readonly initialValues!: Plain | null
  @Prop({type: Boolean, default: true}) readonly canCreate!: boolean
  /**
   * Sem a folga de página em volta: o quadro está dentro de outra tela.
   *
   * É o caso do bloco de um relacionamento na ficha, onde a folga já é a do bloco — somar as
   * duas empurrava o cabeçalho e as raias 16px para dentro, desalinhados da tabela vizinha.
   */
  @Prop({type: Boolean, default: false}) readonly flush!: boolean
  /**
   * Onde desenhar o cabeçalho, se não aqui: o seletor de um elemento da tela de quem hospeda. A
   * ficha o leva para a linha das abas, ao lado da Lista/Kanban, em vez de uma linha só dele
   * acima das raias (pedido do usuário).
   */
  @Prop({type: String, default: ''}) readonly headerTo!: string

  /** O modal de escolher e ordenar as dimensões. */
  dimensionsIsVisible = false
  /**
   * Vínculo que só o host sabe fazer, chamado ANTES de recarregar. Serve ao
   * relacionamento múltiplo direto, em que a lista mora no dono e o filho recém-criado
   * não entraria no filtro sem que o dono fosse atualizado. Função e não evento porque
   * precisa ser aguardada — $emit não dá para esperar.
   */
  // eslint-disable-next-line no-unused-vars
  @Prop({type: Function, default: null}) readonly linkCreated!: ((added: any) => Promise<void>) | null
  /**
   * O "+" da raia entregue a quem hospeda, em vez de abrir a janela de criar deste quadro.
   *
   * É o quadro de um relacionamento na ficha do dono: lá o "+" da raia faz o mesmo que o
   * "Adicionar" da seção — escolher entre criar e vincular existente, com o dono travado (pedido do
   * usuário). O quadro diz só o que é dele: onde foi o clique, com que valores o item nasce nesta
   * raia, e quem MORA nela — para a escolha de existentes oferecer só esses ("se a raia é Samba, só
   * aparecem sambas"), da coleção inteira e não só os já vinculados.
   */
  // eslint-disable-next-line no-unused-vars
  @Prop({type: Function, default: null}) readonly addInLane!: ((element: Element, lane: LaneAdd) => void) | null
  /**
   * O X de cada card, que tira o item do dono — o mesmo do quadro de relacionamento na ficha, entregue
   * a quem hospeda pelo mesmo motivo do `addInLane`: só a ficha sabe de que dono se tira, pergunta e
   * grava. Sem ela, o card não tem X.
   */
  // eslint-disable-next-line no-unused-vars
  @Prop({type: Function, default: null}) readonly unlinkItem!: ((instance: Instance) => void) | null
  /**
   * A lixeira em cada card, que exclui o item. Do quadro da COLEÇÃO; o de um relacionamento desliga,
   * porque ali o lugar é do X de desvincular (pedido do usuário: "X ou lata de lixo").
   */
  @Prop({type: Boolean, default: true}) readonly deletes!: boolean

  /** O item que a lixeira de um card pediu para excluir, à espera da confirmação. */
  deleting: Instance | null = null

  instances: Instance[] = []
  /**
   * Os itens de cada coleção apontada, por alias — o `relDomains` do servidor.
   *
   * É o que dá raia a um valor que ainda não tem item nenhum. Vazio enquanto não se pede, e o
   * quadro funciona igual: sem ele as raias são só as dos valores presentes, que era a regra
   * antiga.
   */
  candidates: Candidates = {}
  /** Os metadados dos reversos, que vêm junto dos candidatos — e junto seguem para o criar. */
  reverseInfo: any = null
  /**
   * Já lemos os candidatos?
   *
   * Precisa ser um sinal próprio. O `reverseInfo` parecia servir e NÃO serve: ele só tem
   * conteúdo para relacionamento REVERSO, então numa coleção cujos relacionamentos são todos
   * diretos ele volta vazio mesmo tendo sido lido — e a entrega nunca acontecia. O `candidates`
   * vazio tem o mesmo problema ao contrário: entregá-lo sem ter lido diria "não há candidato
   * nenhum", e o formulário aceitaria a mentira em vez de ir buscar.
   */
  knowsRelated = false
  /** Os blocos montados: um por combinação das dimensões posteriores. */
  blocks: BlockModel[] = []
  periods: KanbanPeriods = {}
  selectedFields: string[] = []
  /** As chaves das dimensões escolhidas, na ordem. A primeira são as raias. */
  dimensionKeys: string[] = []
  /** Os valores visíveis de cada dimensão, por chave. */
  visibleValues: VisibleValues = {}
  /** A ordem e o conjunto de raias de cada relacionamento, como na carga — ver o freezeGroups. */
  frozenGroups: { [key: string]: FrozenGroups } = {}
  /** Bloco sem item nenhum fica escondido até se pedir o contrário. */
  showEmptyBlocks = false
  createIsVisible = false
  /** O item que a janela de criar acabou de criar, para destacá-lo quando ela fechar. */
  createdId: number | null = null
  /**
   * Os valores que o "+" da raia semeia, ou null quando veio do cabeçalho.
   *
   * Já são valores de formulário, e não uma coordenada: o grupo "Não definido" não semeia campo
   * nenhum, e é o `seedValues` que faz essa tradução.
   */
  createSeed: Plain | null = null
  /** Instância com uma mudança em curso pelo atalho do card. */
  movingInstance: number | null = null
  /** A raia de onde o card saiu no arrasto em curso. Vazia fora de um gesto. */
  dragOrigin: Coordinate = {}
  /** O gesto esperando "mover ou acrescentar", ou null quando não há pergunta na tela. */
  asking: {instance: Instance, target: Coordinate, origin: Coordinate} | null = null
  /** Instância cujo valor está sendo escolhido no modal, ou null. */
  pickingFor: Instance | null = null
  /**
   * Por qual dimensão o modal está trocando. Volta para a das RAIAS a cada abertura: é a que o
   * quadro está mostrando como principal, e quem abre o atalho quase sempre quer ela.
   */
  pickingKey = ''
  /** De que raia o modal de mover foi aberto — é o que "mover" substitui num múltiplo. */
  pickingFrom: Coordinate = {}
  /** Largura útil do tabuleiro, observada. É ela que decide o `sliding`. */
  boardWidth = 0
  /**
   * Onde o quadro está deslizado, em pixels — um valor só, para todos os blocos.
   *
   * Mora aqui porque é de todos: os blocos são as linhas de uma grade, e a mesma raia tem de
   * ficar na mesma coluna em todos eles. Cada um rolando por conta própria desalinharia a
   * grade, e ler o quadro na vertical — que é o que as dimensões existem para permitir —
   * deixaria de funcionar.
   */
  slideOffset = 0
  /** Desfaz a observação de tamanho; null enquanto não há nenhuma. */
  private stopSizeWatch: (() => void) | null = null

  //Sobrescreve o getter da base, que leria $route.params.idea
  get ideaAlias() {
    return this.context.idea
  }

  /**
   * O endereço das preferências deste quadro, pronto para espalhar nas chamadas do storage.
   *
   * Tupla e não objeto porque é ela que vai no lugar dos três primeiros parâmetros de cada
   * método — antes cada uma das oito chamadas redesestruturava o `context` para dizer a mesma
   * coisa, e o recorte teria de ser acrescentado em oito lugares.
   */
  private get prefs(): [string, string, string] {
    return [this.context.system, this.context.idea, this.scope]
  }

  //Cada host decide: a página abre modal, o modal empilha outro. Com o que o quadro já tem do item, para
  //a ficha mostrar já — ver `previewOfOpened` —, e os candidatos, para a edição (o lápis do card) abrir
  //sem ir ao servidor
  openInstance(context: InstanceContext) {
    this.$emit('open-instance', context, previewOfOpened(context, this.instances, this.idea, this.relatedRead))
  }

  /**
   * As faixas deslizam, ou dividem a largura entre si? Conforme o espaço REAL dividido pelo
   * número delas — a regra mora no utils/kanbanLayout, que explica por que o breakpoint do
   * Vuetify não serve aqui.
   *
   * Pelo número de raias VISÍVEIS, e não pelas do primeiro bloco: com os vazios escondidos
   * pode não haver bloco nenhum, e `blocks[0]` estouraria. O layout também não deve depender
   * de haver dado.
   *
   * Serve a duas coisas, e a segunda é o motivo de ela viver aqui e não dentro do bloco: além
   * do desenho, é ela que liga o atalho de mover no card. Deslizando, há raia fora da janela, e
   * arrastar até ela não é gesto que se faça — do mesmo jeito que não era quando empilhava.
   */
  get sliding(): boolean {
    return lanesSlide(this.boardWidth, this.visibleOf(this.laneDimension).length)
  }

  /**
   * Entrar ou sair do modo deslizante zera a posição.
   *
   * Sem faixas deslizando não há posição que valha, e os containers nascem no começo quando ela
   * volta. Guardar o número de antes deixaria o tabuleiro achando que o quadro está deslocado
   * enquanto os blocos estão no zero — e o primeiro gesto daria um salto.
   */
  @Watch('sliding')
  onSlidingChange() {
    this.slideOffset = 0
  }

  /**
   * Observa o próprio elemento, e não a janela: o tabuleiro também é aberto dentro do
   * modal do Kanban, onde o tamanho da janela não diz nada sobre o espaço que ele tem.
   * Foi o que a página zoomada revelou — com a página reduzida, o CSS enxerga uma
   * largura que a janela não tem.
   *
   * O hook convive com o `mounted` da classe-base: o Vue encadeia os dois, o do pai
   * primeiro. `async` só para casar com a assinatura dele.
   */
  async mounted() {
    this.measure()
    this.stopSizeWatch = watchSize(this.$el as HTMLElement, () => this.measure())
    //O que este quadro leu, oferecido a quem criar na coleção dele — o "Criar" da faixa do caminho
    //mora no casco, e é por aqui que a janela abre sem ir ao servidor. Ver o `offerKnown`.
    this.stopOffering = offerKnown(this.context.idea, () => this.relatedKnown)
  }

  private stopOffering: (() => void) | null = null

  /**
   * Entrega a leitura para a visão irmã, se for para lá que se está indo — ver
   * `handOverCollection`. Só no quadro da PÁGINA: com os itens vindos de fora, quem leu foi o
   * hospedeiro, e esta leitura não é desta tela para entregar.
   */
  beforeUnmount() {
    this.stopSizeWatch?.()
    this.stopOffering?.()
    if (!this.items)
      handOverCollection(this.context, this.$route?.name as string, this.read, this.arrivedAt)
  }

  measure() {
    this.boardWidth = (this.$el as HTMLElement).clientWidth
  }

  /**
   * Grupo de arrasto por coleção: dois tabuleiros abertos ao mesmo tempo (página e
   * modal, ou modais aninhados) não podem trocar cards entre coleções diferentes.
   */
  get dragGroup(): string {
    return `instances:${this.context.system}/${this.context.idea}`
  }

  contextOf(instance: Instance): InstanceContext {
    return {...this.context, instance: instance._instance}
  }

  /**
   * A rota em que este quadro montou — ver o `from` do `handOverCollection`.
   *
   * Guardada na montagem porque no `beforeUnmount` o `$route` já é o de quem entra: sem ter com
   * que comparar, uma remontagem no lugar parece uma viagem para a visão irmã.
   */
  private arrivedAt: string | null = null

  async load() {
    this.arrivedAt = (this.$route?.name as string) || null
    await this.loadInstances()
    this.freezeGroups()
    //Quem navega é o host: uma folha dentro de um modal não pode empurrar rota. O portão
    //deixou de ser "tem fluxo" e passou a ser "tem por onde agrupar": uma coleção com um
    //atributo de seleção e sem fluxo nenhum tem Kanban.
    this.loadDimensions()
    if (!this.dimensions.length) return this.$emit('no-dimensions')
    this.loadPeriods()
    this.loadFields()
    this.loadValues()
    this.regroup()
  }

  /**
   * Os itens do quadro e os candidatos das dimensões, numa ida ao servidor ou nenhuma.
   *
   * **Com os itens prontos**, a lista vem copiada: o arrasto altera os ITENS, que são
   * compartilhados de propósito — a tabela do hospedeiro mostra a mudança sem reler —, mas
   * reordenar ou remover do array é assunto deste quadro, e mexer no array do hospedeiro o faria
   * redesenhar no meio do gesto. Aí a requisição, se houver, é só pelos candidatos: projeção
   * mínima, mesmo recorte, corpo praticamente vazio.
   *
   * **Listando**, é a leitura que a visão irmã também faz — ver `collectionRead` —, e os
   * candidatos pegam carona nela.
   */
  private async loadInstances() {
    const {system: systemAlias, idea: ideaAlias} = this.context
    if (this.items) {
      this.instances = [...this.items]
      //O hospedeiro já tem os candidatos: foi este mesmo quadro que os leu, numa montagem
      //anterior, e os devolveu para cima. Alternar Lista ↔ Kanban dentro da ficha deixa de ir
      //ao servidor — a mesma promessa que a listagem e o quadro da coleção já cumprem, aqui
      //por PROP em vez de entrega entre rotas, porque dentro de um modal é assim que o dado
      //anda (ver o `utils/handover`, que recusa este caso de propósito).
      if (this.knownCandidates) {
        this.adoptRelated(this.knownCandidates)
        return
      }
      //Nunca faz esperar, nem pelas raias. Os candidatos acrescentam as raias VAZIAS — as que
      //têm item já vêm dos próprios itens —, e segurar o desenho por elas era a barra de
      //progresso ao trocar de aba. Chegando depois, entram como raia nova, que é o que o
      //`withFrozenGroups` já sabe fazer.
      if (this.candidatesForDimensions || this.candidatesForCreate) this.warmCandidates()
      return
    }
    //A visão irmã pode ter acabado de ler isto: alternar Lista ↔ Kanban não vai ao servidor.
    //Quem chega por URL, F5 ou voltar não encontra nada e lê, como sempre.
    //
    //Só sem recorte: a entrega é da coleção inteira, e um quadro recortado — o de um
    //relacionamento — leria outro conjunto. Aceitá-la ali mostraria itens que não são dele.
    const given = this.filter ? null : takeCollectionRead(this.context)
    //SEMPRE com os candidatos, mesmo sem motivo aparente: esta é a leitura compartilhada com a
    //listagem, e ela sempre os pede. Pedindo condicionalmente, uma coleção SEM relacionamento
    //nenhum lia sem eles, a entrega era recusada pela guarda — que não sabe distinguir "não li"
    //de "não há o que ler" — e trocar de Kanban para Lista voltava ao servidor. Duas visões da
    //mesma coisa só são intercambiáveis se fizerem a MESMA requisição.
    //
    //E não custa: o servidor varre as propriedades de relacionamento para montá-los, então uma
    //coleção sem nenhuma devolve o mapa vazio sem consulta alguma.
    const res = given || await this.$instances.list(
        {systemAlias, ideaAlias}, collectionReadConfig(true, this.filter))
    this.read = res
    this.instances = res.value as Instance[]
    this.adoptRelated(res)
  }

  /**
   * Só o que vem AO LADO dos itens: os candidatos, sem reler o que já se tem.
   *
   * `properties` mínima porque o corpo de cada item não interessa — o recorte e a coleção são
   * os mesmos, para a resposta falar dos mesmos itens.
   */
  private async readRelated() {
    const {system: systemAlias, idea: ideaAlias} = this.context
    const res = await this.$instances.list({systemAlias, ideaAlias},
        {...collectionReadConfig(true, this.filter), meta: [], properties: ["_instance"]})
    this.adoptRelated(res)
  }

  /**
   * Busca os candidatos SEM fazer ninguém esperar, para o "+" abrir sem ir ao servidor.
   *
   * É a diferença entre a espera que se vê e a que não se vê. Esperar por eles aqui era a barra
   * de progresso na troca de aba — parte da tela sumindo por uma requisição que nada do que está
   * à vista precisa. Começar e não esperar dá o mesmo ganho no "+" sem custo nenhum na troca:
   * quando o clique chega, eles já estão em mão; se chegar antes, o formulário lê por conta
   * própria, que é o que sempre fez.
   *
   * Sem `await` de propósito — o `catch` existe porque uma promessa solta que falha vira erro
   * não tratado no console, e esta falha não tem o que avisar: o formulário lê de novo.
   */
  private warmCandidates() {
    this.readRelated().catch(() => undefined)
  }



  /**
   * Os candidatos de que o QUADRO precisa para se desenhar: uma dimensão de relacionamento dá
   * raia a um valor sem item nenhum, e sem a lista deles essas raias não existiriam.
   *
   * **Só vale COM os itens vindos de fora** — o quadro de um relacionamento dentro da ficha.
   * Listando, a leitura é a compartilhada com a listagem e já traz os candidatos sempre.
   *
   * Por isso este motivo é o único que faz esperar. Pergunta às PROPRIEDADES, e não ao
   * `allDimensions`, para não girar em círculo: é o `allDimensions` que consome o resultado disto.
   */
  private get candidatesForDimensions(): boolean {
    const fixed = fixedByFilter(this.filter)
    return (this.idea.properties || []).some(property =>
        property.relationship && !fixed.includes(property.alias) &&
        isDimensionProperty(property, this.idea.alias, this.workspace.ideas))
  }

  /**
   * Os candidatos de que o FORMULÁRIO de criar precisa, para os campos de relacionamento.
   *
   * Também só com os itens vindos de fora, pelo mesmo motivo. Nada do que está à vista depende
   * deles, então este nunca faz esperar: aquece depois — ver `warmCandidates`.
   */
  private get candidatesForCreate(): boolean {
    return this.offersCreate && this.hasRelationships
  }

  /**
   * O quadro oferece o "+" de criar?
   *
   * Duas condições: o hospedeiro deixa — há quadro que não cria, como o de um relacionamento
   * sem permissão — E o ambiente aceita alteração. A segunda é do ambiente inteiro e não cabia
   * na prop, que é do hospedeiro; juntas aqui, os dois lugares que perguntam leem a mesma
   * resposta, inclusive o que decide buscar candidatos para o formulário de criar.
   */
  get offersCreate(): boolean {
    return this.canCreate && this.canCreateIn(this.idea)
  }

  /** A coleção aponta para alguma outra? Sem isso não há candidato nenhum a buscar. */
  private get hasRelationships(): boolean {
    return (this.idea.properties || []).some(property => property.relationship)
  }

  /**
   * O hospedeiro recarregou os itens: readota e reagrupa.
   *
   * NÃO dispara no arrasto, e é isso que preserva o comportamento de sempre — o quadro não
   * reagrupa depois de soltar um card. O arrasto altera os itens no lugar; quem troca o array é
   * só uma releitura de verdade, como a que segue a criação.
   */
  @Watch('items')
  async onItemsChange() {
    if (!this.items) return
    await this.load()
  }

  /**
   * Nasceu um item desta coleção por outra porta — o "+" da barra, o da faixa do caminho (ver o
   * `requestCreation`). O "+" da raia tem janela própria, porque semeia os valores dela, e relê
   * pelo `onCreated`; os outros passam pela janela do casco, e é por este aviso que o quadro sabe.
   */
  get createdNotice() {
    return lastCreated.value
  }

  @Watch('createdNotice')
  async onCreatedNotice(notice: { idea: string } | null) {
    if (notice && notice.idea === this.context.idea) await this.reload()
  }

  /**
   * A resposta do servidor como veio, intocada, para a visão irmã receber o que receberia dele.
   *
   * O `instances` não serve para isso: o arrasto o altera, e o quadro copia o array. Guardar a
   * resposta é o que mantém a entrega igual a uma leitura.
   */
  private read: any = null

  /** Guarda o que veio sobre os relacionamentos, e marca que agora se sabe. */
  private adoptRelated(res: any) {
    this.candidates = res.relDomains || {}
    this.reverseInfo = res.reverseInfo
    this.knowsRelated = !!res.relDomains
    //Sobe para quem sobrevive à troca de aba. Só o que foi LIDO: devolver o que veio por prop
    //seria o hospedeiro guardando de novo o que já guardava.
    if (res !== this.knownCandidates) this.$emit('candidates', res)
  }

  /**
   * O que este quadro sabe dos relacionamentos, na forma que o formulário de criar espera.
   *
   * A montagem é a mesma da listagem — são duas visões da mesma coleção —, e por isso mora no
   * `knownForCreate`. Daqui sai só o que é deste quadro: os candidatos, quando lidos.
   */
  private get relatedKnown(): any {
    const related = this.relatedRead
    return knownForCreate(this.workspace, this.context.idea, related && related.relDomains, this.reverseInfo)
  }

  /**
   * Os candidatos e os reversos que este quadro leu — o que os formulários precisam além do item: o de
   * criar (`relatedKnown`) e o de editar (`openInstance`). Null enquanto não leu.
   */
  private get relatedRead(): RelatedRead | null {
    //"Não li" e "não há o que ler" davam no mesmo, e não são a mesma coisa: numa coleção SEM
    //relacionamento o quadro não pede `rel-domains` porque não há candidato nenhum — e o mapa
    //vazio é a verdade sobre ela, não a ignorância do quadro. Tratados como um, o formulário
    //ia ao servidor buscar o nada, que é a requisição que sobrou em produção.
    const known = this.knowsRelated || !this.hasRelationships
    return known ? {relDomains: this.candidates, reverseInfo: this.reverseInfo} : null
  }

  /**
   * Criar a partir do "+" de uma faixa, que é o único lugar de onde se cria no quadro.
   *
   * A coordenada e a raia vêm sempre: o item nasce onde foi pedido. Havia um segundo caminho,
   * o "+" do cabeçalho, que não semeava nada — saiu, e com ele os parâmetros opcionais que
   * existiam só para ele.
   */
  openCreate(coordinate: Coordinate, lane: LaneModel, element?: Element) {
    const target = {...coordinate, [this.laneDimension.key]: lane.id}
    //Pelo seedValues: nascer no grupo "Não definido" é nascer sem o campo, não com o sentinela.
    const seed = seedValues(target, this.dimensions)
    if (this.addInLane && element) {
      const dimensions = this.dimensions
      return this.addInLane(element, {
        seed,
        accepts: (item: Instance) => inCoordinate(item, target, dimensions),
        values: coordinateValues(target, dimensions)
      })
    }
    this.createSeed = seed
    this.createIsVisible = true
  }

  /**
   * Fecha a janela de criar e, se ela criou, mostra onde o item novo foi parar.
   *
   * Só agora, e não no `onCreated`: a janela continua aberta depois de salvar, virando a ficha do
   * item, e o card nasce atrás do fundo escuro. O destaque dado lá apagaria sem ninguém ver.
   */
  closeCreate() {
    this.createIsVisible = false
    this.createSeed = null
    const created = this.createdId
    this.createdId = null
    if (created === null) return
    const card = (this.$el as Element).querySelector(`[data-card="${created}"]`)
    pulse(card?.firstElementChild as HTMLElement | null)
  }

  /**
   * O que o item nasce preenchendo: o vínculo com o dono, quando há, mais a semente da raia.
   *
   * O `createSeed` é nulo enquanto o modal está fechado, e por isso a guarda continua aqui —
   * o getter é lido antes de o "+" ser tocado.
   */
  get createValues(): Plain | null {
    if (!this.createSeed) return this.initialValues
    return {...(this.initialValues || {}), ...this.createSeed}
  }

  /**
   * O que a janela precisa para abrir já criando: o que o item herda da faixa, mais o que este
   * quadro JÁ LEU sobre os relacionamentos.
   *
   * Sem o segundo, o formulário repetiria o `rel-domains` — uma consulta por coleção apontada —
   * que é o carregamento demorado ao abrir. Vai quando este quadro o pediu, e também quando não
   * havia o que pedir; senão o formulário lê, como sempre. Ver o `relatedKnown`.
   */
  get creating(): any {
    return {
      initialValues: this.createValues,
      known: this.relatedKnown
    }
  }

  /** Fecha o modal ANTES de gravar: o alvo escolhido já não depende mais dele. */
  openPicker(instance: Instance, from: Coordinate) {
    this.pickingFor = instance
    this.pickingFrom = from
    this.pickingKey = this.laneDimension.key
  }

  /**
   * A dimensão que o modal está mexendo — a escolhida no campo, ou a das raias.
   *
   * Cai de volta na das raias quando a chave não nomeia mais nada: o modal fica aberto enquanto
   * o quadro vive, e as dimensões escolhidas podem mudar embaixo dele.
   */
  get pickingDimension(): Dimension {
    return this.dimensions.find(dimension => dimension.key === this.pickingKey) || this.laneDimension
  }

  /**
   * O cabeçalho do modal de troca rápida: o item no título, "Editar {coleção}" embaixo.
   *
   * Pelo `instanceEdit`, o mesmo do formulário — porque é disso que se trata: a janela existe
   * para MUDAR um valor, e "Demanda" sozinho dizia só de que coleção o item é, que o quadro em
   * volta já diz. Aberto de um card entre dezenas, o nome do campo sozinho também não dizia de
   * quem era o valor; o acento sai daí junto, na cor daquele item.
   */
  get pickerHeading(): Heading {
    const instance = this.pickingFor as Instance
    return instanceEdit(instance, this.idea, instance._instance)
  }

  /** O que o modal precisa saber de cada dimensão para oferecê-las: o nome e a chave. */
  get dimensionChoices(): DimensionChoice[] {
    return this.dimensions.map(dimension => ({key: dimension.key, name: dimension.name}))
  }

  async onPickState(state: any) {
    const instance = this.pickingFor as Instance
    const from = this.pickingFrom
    const dimension = this.pickingDimension
    this.pickingFor = null
    await this.moveTo(instance, state, from, dimension)
  }

  /**
   * O conjunto marcado no modal, numa dimensão múltipla.
   *
   * **Não passa pelo `moveTo`, e portanto não pergunta "mover ou acrescentar"**: marcando as
   * caixas o usuário declarou o conjunto final, e não há ambiguidade a resolver. A pergunta
   * continua no arrasto, onde o gesto de fato tem duas leituras — soltar um card numa raia pode
   * querer dizer as duas coisas, e marcar uma caixa não.
   *
   * Reagrupa sempre: a dimensão é múltipla, então o card pode ter acabado de nascer ou sumir de
   * várias raias ao mesmo tempo.
   */
  async onSaveValues(ids: number[]) {
    const instance = this.pickingFor as Instance
    const dimension = this.pickingDimension
    this.pickingFor = null
    if (this.movingInstance !== null) return
    this.movingInstance = instance._instance
    try {
      await this.saveChanges(instance, changesForValues(instance, dimension, ids), true)
    } finally {
      this.movingInstance = null
    }
  }

  createLabelFor(lane: LaneModel): string {
    return this.$tt('instance-create-in', {collection: this.idea.name, value: lane.name}) as string
  }

  /**
   * Criou pelo "+" de uma faixa.
   *
   * A janela NÃO se fecha: ela própria troca para a ficha do item criado. Aqui ficou só o que é
   * do quadro — o vínculo com o dono e a releitura das raias.
   */
  async onCreated(added: any) {
    this.createdId = added._instance
    //Vincular antes de recarregar, senão o filho novo não cairia no filtro
    if (this.linkCreated) await this.linkCreated(added)
    await this.reload()
  }

  /**
   * Recarrega o tabuleiro. Público: o host chama depois de vincular um item novo.
   *
   * Com os itens vindos de fora quem relê é o hospedeiro, e o `@Watch('items')` faz o resto —
   * chamar o `load` aqui só readotaria a lista velha.
   */
  async reload() {
    if (this.reloadItems) return await this.reloadItems()
    await this.load()
    this.$emit('changed')
  }

  /**
   * As dimensões escolhidas. Null é "nunca escolhi" e cai no padrão — a situação quando há
   * fluxo, senão a primeira elegível, que é o que mantém o quadro de sempre para quem nunca
   * abriu o modal.
   */
  loadDimensions() {
    this.dimensionKeys = this.$storage.kanbanDimensions(...this.prefs) || []
  }

  /** Os valores visíveis de cada dimensão, guardados por dimensão — ver o `visibleOf`. */
  loadValues() {
    this.visibleValues = this.$storage.kanbanValues(...this.prefs) || {}
  }

  /**
   * Prazo salvo por coleção; raia terminal sem preferência começa em uma semana.
   *
   * Só a dimensão de situação produz raia terminal — o prazo depende do carimbo de quando o
   * item entrou ali, e não existe equivalente para "quando mudou o valor de uma seleção". Numa
   * dimensão que não é situação o mapa sai vazio e o menu de prazo não aparece.
   */
  loadPeriods() {
    const finals = this.laneDimension.values.filter(value => value.final).map(value => value.id)
    this.periods = periodsFor(finals, this.$storage.kanbanPeriods(...this.prefs))
  }

  /**
   * Campos extras do card. O null distingue "nunca escolhi" (cai no padrão) de
   * "escolhi nenhum"; aliases que sumiram do design da coleção são descartados.
   */
  loadFields() {
    const stored = this.$storage.kanbanFields(...this.prefs)
    const available = this.fieldOptions
    this.selectedFields = stored === null
        ? this.defaultFields(available)
        : stored.filter(alias => available.some(property => property.alias === alias))
  }

  /**
   * A ordem e o conjunto de raias de cada dimensão de relacionamento, desenhados AGORA.
   *
   * Desenhados na carga e não mais: ver o `withFrozenGroups` para o porquê. Vazio primeiro de
   * propósito — é o que faz o `allDimensions` desta leitura devolver a ordem crua, que é
   * justamente a foto que se quer tirar. Uma carga de verdade redesenha; mover um card, não.
   *
   * Só o relacionamento: a ordem da situação e a das opções de um domínio são do desenho do
   * usuário, e não têm o que congelar.
   */
  private freezeGroups() {
    this.frozenGroups = {}
    const photo: { [key: string]: FrozenGroups } = {}
    this.allDimensions
        .filter(dimension => dimension.kind === 'rel')
        .forEach(dimension => photo[dimension.key] = frozenGroupsOf(dimension))
    this.frozenGroups = photo
  }

  /**
   * O catálogo de dimensões desta coleção: a situação, quando há fluxo, e as propriedades
   * elegíveis. É recalculado a cada mudança das instâncias porque a dimensão de relacionamento
   * é feita dos valores PRESENTES entre elas.
   *
   * Recalculado, mas não reordenado: a foto da carga é reaplicada aqui, senão a contagem nova
   * mandaria as raias para outro lugar a cada card movido.
   */
  get allDimensions(): Dimension[] {
    //Fora as que o recorte já prende a um valor só: no Kanban de um relacionamento, o campo
    //que aponta de volta para o dono daria uma raia com tudo dentro. Aqui, e não no
    //`dimensions`, para a opção sumir também do modal de Dimensões — e chave guardada que
    //deixou de existir o `dimensions` já descarta sozinho.
    const fixed = fixedByFilter(this.filter)
    return dimensionsOf(this.idea, this.workspace.ideas, this.workflow, this.workspace,
        this.instances, {
          state: this.$tt('state') as string,
          noValue: this.$tt('no-value') as string
        }, this.candidates)
        .filter(dimension => !fixed.includes(dimension.key))
        .map(dimension => withFrozenGroups(dimension, this.frozenGroups[dimension.key]))
  }

  /**
   * As dimensões escolhidas, na ordem: a primeira são as raias, as demais são os blocos.
   *
   * Chave guardada que não nomeia mais nada — propriedade renomeada, fluxo desassociado — é
   * descartada, e se não sobrar nenhuma cai na primeira elegível. Sem isso o quadro abriria
   * sem dimensão e sem raia, que é a falha que o `loadLanes` já existia para impedir.
   */
  get dimensions(): Dimension[] {
    const chosen = (this.dimensionKeys || [])
        .map(key => this.allDimensions.find(dimension => dimension.key === key))
        .filter(dimension => !!dimension) as Dimension[]
    return chosen.length ? chosen : this.allDimensions.slice(0, 1)
  }

  /**
   * O título do quadro: a coleção, e também a PROPRIEDADE quando as raias não são a situação.
   *
   * Agrupado por situação, o quadro é "o" Kanban da coleção, e "Tarefas" basta — foi assim que
   * ele nasceu, e continua sendo o caminho de todo mundo. Agrupado por outra coisa ele é um
   * dentre vários possíveis, e sem o nome da dimensão duas telas bem diferentes — Tarefas por
   * Sistema e Tarefas por Prioridade — se chamam igual.
   *
   * A dimensão das RAIAS, e não as de bloco: é ela que decide a leitura do quadro, e enfileirar
   * todas as escolhidas daria um título que cresce com o que o usuário marcou no menu.
   *
   * Vale só onde o título é o padrão do tabuleiro, que é a rota. O quadro de um relacionamento
   * e o bloco da ficha mandam o deles pelo slot `#title`, e esses já dizem de que propriedade
   * tratam — ver o RelationshipBoard.
   */
  get boardTitle(): string {
    return this.laneDimension.kind === 'state' ? this.idea.pluralName
        : within(this.idea.pluralName, this.laneDimension.name)
  }

  /**
   * A dimensão desenhada como raias.
   *
   * O vazio existe e não é defensivo à toa: entre o `load` descobrir que não há por onde
   * agrupar e o hospedeiro navegar para fora, a tela ainda desenha um quadro. Sem ele, o
   * `sliding` e o menu de raias desreferenciariam `undefined` nesse intervalo.
   */
  get laneDimension(): Dimension {
    return this.dimensions[0] || EMPTY_DIMENSION
  }

  /**
   * Os valores visíveis de cada dimensão. Mesmo contrato dos campos: ausente é "nunca escolhi" e
   * cai no PADRÃO da dimensão — todos, menos no relacionamento, onde são os que têm item (ver
   * `defaultValues`). É também como se guarda a escolha igual ao padrão, para o valor criado
   * depois entrar sozinho.
   *
   * Filtra a lista da DIMENSÃO pelo que está escolhido, e não o contrário: assim a ordem é
   * sempre a dela e valor que sumiu se perde sozinho. Se isso esvaziar, volta a todos — um
   * quadro sem faixa nenhuma não é escolha de ninguém.
   */
  visibleOf(dimension: Dimension): number[] {
    const chosen = this.visibleValues[dimension.key] || defaultValues(dimension)
    const kept = allValues(dimension).filter(id => chosen.includes(id))
    return kept.length ? kept : allValues(dimension)
  }

  /** O mapa que o agrupamento consome, com uma entrada por dimensão em uso. */
  get visible(): VisibleValues {
    const values: VisibleValues = {}
    this.dimensions.forEach(dimension => values[dimension.key] = this.visibleOf(dimension))
    return values
  }

  /**
   * Cada item do menu é o VALOR da raia, desenhado como ele aparece no resto do app: régua para
   * item de coleção, bolinha para situação, chip para opção de domínio. Ver o `formOf`.
   *
   * Era a miniatura da raia — fundo claro, caixa e nome no tom dela. Parecia a escolha certa
   * porque o menu fala de raias, mas punha a cor no papel de SUPERFÍCIE para qualquer natureza
   * de valor, e é justamente esse papel que o item de coleção não aceita: ali a cor é mancha, e
   * tingir o fundo obriga o nome a escurecer. A forma passou a dizer a natureza, e a linha
   * ficou neutra — a cor já está no valor, e repeti-la no fundo e na caixa a diria três vezes.
   */
  menuItemsOf(dimension: Dimension): CheckMenuItem[] {
    return dimension.values.map(value => ({
      value: value.id,
      title: value.name,
      option: value,
      form: formOf(dimension)
    }))
  }

  /** O menu do cabeçalho fala da primeira dimensão; o modal, das demais. */
  get laneMenuItems(): CheckMenuItem[] {
    return this.menuItemsOf(this.laneDimension)
  }

  /** Veio do modal: a ordem das dimensões e os valores visíveis de cada uma, de uma vez. */
  onDimensionsSave({keys, visible}: { keys: string[], visible: VisibleValues }) {
    this.dimensionKeys = keys
    //Pelo storedValues: o modal manda o mapa completo, e sem isso a dimensão com todos os
    //valores marcados guardaria a lista de hoje em vez de "todos".
    this.visibleValues = storedValues(visible, this.allDimensions)
    this.$storage.setKanbanDimensions(...this.prefs, keys)
    this.$storage.setKanbanValues(...this.prefs, this.visibleValues)
    this.dimensionsIsVisible = false
    //O prazo é por raia terminal, e a primeira dimensão pode ter mudado
    this.loadPeriods()
    this.regroup()
  }

  onLanesChange(lanes: number[]) {
    this.onValuesChange(this.laneDimension, lanes)
  }

  /**
   * A escolha de valores de UMA dimensão — o menu de raias fala da primeira, o modal das
   * demais, e os dois chegam aqui.
   */
  onValuesChange(dimension: Dimension, values: number[]) {
    //O `min` do menu já impede desmarcar o último; isto guarda o INVARIANTE, para o quadro
    //nunca ficar sem faixa nem sem bloco por um caminho que a trava não cubra.
    if (!values.length) return
    this.visibleValues = withValues(this.visibleValues, dimension, values)
    this.$storage.setKanbanValues(...this.prefs, this.visibleValues)
    this.regroup()
  }

  /**
   * As vagas do padrão vão para quem NÃO faz parte do identificador: a chave já
   * aparece no cabeçalho do card, e gastar as três com ela seria repetir o que está
   * uma linha acima. Continua escolhível no menu, só não marcada de saída.
   *
   * O resultado sai na ordem de declaração da coleção, e não na da preferência —
   * mesmo invariante que o onFieldsChange preserva.
   */
  private defaultFields(available: IdeaProperty[]): string[] {
    //A situação fica de fora do padrão: ela é a raia na maioria dos quadros, e o card repetiria
    //o que a coluna em volta dele já diz. Continua a um clique no menu.
    const options = available.filter(property => property.alias !== STATE_KEY)
    const preferred = [
      ...options.filter(property => !property.identifier),
      ...options.filter(property => property.identifier)
    ].slice(0, DEFAULT_FIELD_COUNT)
    return options.filter(property => preferred.includes(property)).map(property => property.alias)
  }

  /**
   * Tudo o que o design não escondeu, inclusive a chave: ela é escolhível.
   *
   * **A situação entra no fim, e não é propriedade.** Ela mora em `_state`, fora de
   * `idea.properties` — é a mesma assimetria que faz a dimensão de situação existir —, e aqui
   * entra como uma propriedade de mentira com o alias `_state`, que é o `STATE_KEY`. É o mesmo
   * caminho que a listagem faz com a coluna dela: lá a situação é uma pseudo-coluna de chave
   * `_state`, desenhada à parte.
   *
   * No FIM, e por isso fora do padrão: na maioria dos quadros a situação é a raia, e repeti-la
   * em cada card seria dizer no card o que a coluna em volta já diz. Quem agrupa por outra
   * coisa a liga no menu.
   */
  get fieldOptions(): IdeaProperty[] {
    //Todos: quem vê cada campo é decidido por item, no card (`cardFieldsOf`)
    const own = this.idea.properties
    return this.workflow ? [...own, {alias: STATE_KEY, name: this.$tt('state') as string} as IdeaProperty] : own
  }

  get identifierProperties(): IdeaProperty[] {
    return this.idea.properties.filter(property => property.identifier)
  }

  get fieldMenuItems(): CheckMenuItem[] {
    return this.fieldOptions.map(property => ({value: property.alias, title: property.name}))
  }

  onFieldsChange(aliases: string[]) {
    //A ordem de declaração da coleção já vem do menu, que ordena pelos seus itens
    this.selectedFields = aliases
    this.$storage.setKanbanFields(...this.prefs, aliases)
  }

  /**
   * Título do card: os campos da chave separados e formatados, em vez do
   * _identifier colado pelo backend, onde uma data aparece crua como 2026-03-14.
   */
  titleOf(instance: any): string {
    const parts = this.identifierProperties
        .map(property => displayValues(instance, property)
            .map(value => valueText(property, value, this.workspace))
            .filter(text => !!text)
            .join(', '))
        .filter(text => !!text)
    return parts.length ? parts.join(IDENTIFIER_SEPARATOR) : instance._identifier
  }

  /** Campo só ocupa linha no card se estiver visível para esta instância e tiver valor. */
  cardFieldsOf(instance: any): IdeaProperty[] {
    return this.selectedFields
        //Pelas OPÇÕES, e não por `idea.properties`: a situação é uma delas e não é propriedade.
        .map(alias => this.fieldOptions.find(property => property.alias === alias))
        .filter(property => !!property)
        .map(property => property as IdeaProperty)
        //O campo oculto nem veio do servidor; e não se desenha nem a linha dele
        .filter(property => fieldVisible(instance, property))
        //A situação sempre tem valor: sem `_state` gravado o item está na inicial do fluxo, e é
        //o `currentStateOf` que resolve isso — ver o `stateOptionOf`.
        .filter(property => property.alias === STATE_KEY
            || hasValue(instance, property, this.workspace))
  }

  /**
   * A situação do item como valor colorido, pela MESMA dimensão que desenha as raias dela.
   *
   * Sem conta nova: o `valuesOf` já resolve o item sem `_state` gravado para a situação inicial,
   * e o `values` já traz a cor que o fluxo escolheu. Montar o par à mão aqui seria uma segunda
   * resposta para "em que situação este item está", e as duas poderiam divergir.
   */
  stateOptionOf(instance: Instance): ColoredOption | undefined {
    const dimension = this.allDimensions.find(candidate => candidate.kind === 'state')
    if (!dimension) return undefined
    const id = dimension.valuesOf(instance)[0]
    return dimension.values.find(value => value.id === id)
  }

  /**
   * Reagrupa a partir de this.instances, sem novo request: trocar o prazo é só
   * refazer isto. As instâncias entram por referência, então o _state que o drag
   * mutou continua valendo no reagrupamento seguinte.
   *
   * E leva cada card do lugar antigo ao novo, em vez de redesenhar o quadro de supetão. Passa por
   * aqui tudo o que o arrasto NÃO fez sozinho: o card que volta quando o servidor recusa ou a
   * pergunta é cancelada, o que nasce do "+", o que some ao ser excluído e os vizinhos de cada um.
   */
  regroup() {
    const before = cardPlaces(this.$el as Element)
    this.blocks = blockModels(this.instances, this.dimensions, this.visible, this.periods)
    this.$nextTick(() => animateFrom(this.$el as Element, before))
  }

  /**
   * Tira um item do quadro, sem reler nada.
   *
   * **Comando, e não estado**: quem sabe que o item foi excluído é a janela que o excluiu, e ela
   * mora no hospedeiro — o mesmo arranjo do zoom do diagrama. É também o caminho que o arrasto já
   * usa, mexer no array e reagrupar, com outro verbo.
   *
   * E é mais fiel do que recarregar: a foto das raias é tirada no `load`, então remontar o quadro
   * refotografa, e uma raia que ficou vazia pela exclusão desaparece junto. Mexendo só no item, a
   * foto continua valendo.
   */
  removeInstance(instanceId: number | string) {
    const id = Number(instanceId)
    this.instances = this.instances.filter(instance => Number(instance._instance) !== id)
    this.regroup()
  }

  /**
   * O lápis de um card: a janela do item, aberta JÁ EDITANDO (escolha do usuário). Pelo mesmo caminho
   * do clique no card — o host abre a janela —, com o pedido anotado para ela ler ao nascer.
   */
  editInstance(event: MouseEvent, instance: Instance) {
    openEditing()
    this.onInstanceLink(event, this.contextOf(instance))
  }

  /** O mesmo, sem o clique: o "Editar" da janela de recusa de um movimento. */
  private editRefused(context: InstanceContext) {
    openEditing()
    this.openInstance(context)
  }

  /**
   * Confirmou a lixeira: exclui como a ficha exclui (`EditInstanceService.delete`, que leva junto os
   * arquivos do item), e o card sai do quadro sem reler a coleção — com a animação de saída.
   */
  async onDeleteConfirmed() {
    const instance = this.deleting!
    try {
      await new EditInstanceService(this.context, this.$instances, this.idea).delete(Number(instance._instance))
    } catch (err) {
      //O servidor refaz a checagem; se recusou aqui, a prévia estava defasada
      this.handleError(this.readableError(err))
      return
    }
    this.deleting = null
    this.info(this.$tt('info.delete-item', {name: instance._identifier}))
    this.removeInstance(instance._instance)
    this.$emit('changed')
  }

  /**
   * Relê UM item e o troca no quadro.
   *
   * **Relê, em vez de aplicar as mudanças em memória** — que é o que o arrasto faz. Ali muda uma
   * dimensão e só; aqui veio de um formulário, que mexe em muitos campos e deixa o servidor
   * recalcular o que é dele: computados, situação, o outro lado de um vínculo. Uma requisição
   * pequena, com a MESMA projeção da leitura do quadro, é a diferença entre parecer certo e
   * estar certo.
   *
   * Sem os candidatos: eles já estão aqui, e são a parte cara da leitura — relê-los por causa de
   * um item seria pagar o preço da coleção inteira para atualizar um card.
   *
   * **Item que não está no quadro é ignorado**: a janela navega por dentro, de um item para
   * outro e até para outra coleção, então nem toda mudança que ela anuncia é daqui.
   */
  async refreshInstance(instanceId: number | string) {
    const id = Number(instanceId)
    const at = this.instances.findIndex(instance => Number(instance._instance) === id)
    if (at < 0) return
    try {
      this.instances.splice(at, 1, await this.reread(id))
      this.regroup()
    } catch (err) {
      //Falhar aqui deixa o card velho na tela, e isso é recuperável: a mensagem diz o que houve
      //e qualquer recarga conserta. Derrubar o quadro inteiro por causa de um card seria pior.
      this.handleError(this.readableError(err))
    }
  }

  /** Um item como a leitura do quadro o traz — a MESMA projeção, sem os candidatos (ver `refreshInstance`). */
  private async reread(id: number): Promise<Instance> {
    const {system: systemAlias, idea: ideaAlias} = this.context
    const res = await this.$instances.get({systemAlias, ideaAlias, instanceId: id}, collectionReadConfig(false))
    return res.value as Instance
  }

  /**
   * Depois de um arrasto, o que o servidor recalcula — as calculadas, e o nome do item quando elas o
   * compõem — vem da releitura do item. O arrasto só sabe o que ele mesmo mudou, e "Resende - RJ" ficava
   * "RJ" depois de ir para São Paulo (relato do usuário).
   *
   * No MESMO objeto, e sem reagrupar: o card já está na raia certa, e trocar o objeto ou refazer as
   * listas logo depois do gesto brigaria com o Sortable (ver o `saveChanges`). Só na coleção com
   * calculada — sem ela, o que o arrasto aplicou já é tudo.
   */
  private async recompute(instance: Instance) {
    if (!this.idea.properties.some(property => property.formula)) return
    try {
      const fresh = await this.reread(Number(instance._instance))
      //A calculada que ficou vazia não vem: sem tirar a chave, o card mostraria o valor velho
      for (const key of Object.keys(instance)) if (!(key in fresh)) delete (instance as any)[key]
      Object.assign(instance, fresh)
    } catch (err) {
      //O card fica com o calculado de antes, e qualquer recarga conserta — o movimento já foi gravado
      console.warn('Não foi possível reler o item depois de mover', err)
    }
  }

  /** Bloco vazio fica escondido até o usuário pedir para ver — ver `showEmptyBlocks`. */
  get visibleBlocks(): BlockModel[] {
    return this.showEmptyBlocks ? this.blocks : this.blocks.filter(block => !isEmptyBlock(block))
  }

  /** Quantos blocos o "esconder vazios" está tirando da vista agora. */
  get hiddenBlocks(): number {
    return this.blocks.filter(isEmptyBlock).length
  }

  //Método, e não campo com arrow: viaja como prop até o bloco, e o Vue 3 entrega os métodos
  //já ligados à instância. Num campo, o `this` capturado seria o da instância crua da classe.
  countTitle(lane: LaneModel): string {
    return lane.hiddenCount ? this.$tt('period.hidden', {count: lane.hiddenCount}) as string : ''
  }

  onPeriodChange(lane: LaneModel, period: FinishedPeriod) {
    this.periods[lane.id] = period
    this.$storage.setKanbanPeriods(...this.prefs, this.periods)
    this.regroup()
  }

  /**
   * Um card foi solto numa raia de um bloco.
   *
   * Só `added` interessa. O `removed` dispara na lista de ORIGEM no mesmo gesto e não sabe
   * para onde o card foi — agir nele gravaria duas vezes. O `moved` é reordenar dentro da
   * lista, e o quadro não guarda ordem nenhuma. Cruzar blocos mantendo a mesma raia chega como
   * `added`, porque são listas distintas: é o que faz o grupo de arrasto único funcionar.
   */
  async onCardDrop({event, coordinate, lane}: CardDrop) {
    if (!event.added) return
    const instance = event.added.element as Instance
    const target = {...coordinate, [this.laneDimension.key]: lane.id}
    //Numa dimensão múltipla que MUDA, o gesto tem duas leituras e o quadro não adivinha: pergunta
    //uma vez, porque o destino já é conhecido. Ver o asksHowToMove, que é quem sabe quando.
    if (asksHowToMove(instance, target, this.dimensions)) {
      this.asking = {instance, target, origin: this.dragOrigin}
      return
    }
    await this.moveToTarget(instance, target, false, this.dragOrigin)
  }

  /**
   * Os nomes que a pergunta usa: a coleção, a dimensão, e as raias de ONDE e PARA ONDE.
   *
   * Sem eles a frase seria genérica — "tira da raia de origem" —, e quem está no meio de um
   * gesto quer ler os nomes que está vendo na tela: "tira de Projeto A e atribui a Projeto B".
   *
   * A dimensão é a que o `asksHowToMove` acusou: a múltipla cujo valor muda e em que o card
   * ainda não está. É a mesma pergunta feita duas vezes, e por isso ela está escrita lá.
   */
  /** As duas saídas da pergunta, e o desistir — que não escolhe nenhuma. */
  get moveActions(): Ask[] {
    return [
      {value: 'move', label: this.$tt('move-here') as string, icon: 'mdi-arrow-right', color: 'primary'},
      {value: 'add', label: this.$tt('add-here') as string, icon: 'mdi-plus'},
      {value: '', label: this.$tt('cancel') as string, text: true}
    ]
  }

  /**
   * TODAS as dimensões sobre as quais se pergunta: as múltiplas cujo valor muda e em que o card
   * ainda não está. É a mesma conta do `asksHowToMove`, que é quem decidiu perguntar.
   *
   * Lista, e não uma só: um arrasto entre blocos mexe na raia e no bloco ao mesmo tempo, e as
   * duas podem ser múltiplas. A pergunta continua sendo UMA — é um gesto, com um significado —,
   * mas ela precisa dizer tudo o que vai acontecer.
   */
  get askingDimensions(): Dimension[] {
    const asked = this.asking
    if (!asked) return []
    return this.dimensions.filter(dimension => dimension.multiple
        && asked.target[dimension.key] !== undefined
        && !dimension.valuesOf(asked.instance).includes(asked.target[dimension.key]))
  }

  /** Cada dimensão que muda, com as duas pontas já resolvidas para a tela. */
  get askingMoves() {
    const asked = this.asking
    if (!asked) return []
    return this.askingDimensions.map(dimension => ({
      dimension,
      from: this.askingValue(dimension, asked.origin),
      to: this.askingValue(dimension, asked.target)
    }))
  }

  get askingAbout(): Record<string, string> {
    const names = this.askingDimensions.map(dimension => dimension.name)
    return names.length ? {collection: this.idea.name, dimension: names.join(', ')} : {}
  }

  /**
   * O valor de uma ponta do gesto nas duas formas que a tela sabe desenhar: o item resolvido,
   * quando a dimensão é um relacionamento, e a opção colorida, que serve a todas.
   */
  private askingValue(dimension: Dimension, coordinate: Coordinate | undefined): CoordinateValue {
    return coordinate ? coordinateValue(dimension, coordinate[dimension.key]) : {dimension}
  }

  /** A raia de onde o arrasto saiu, como coordenada — é o que "mover" substitui. */
  originOf({coordinate, lane}: {coordinate: Coordinate, lane: LaneModel}): Coordinate {
    return {...coordinate, [this.laneDimension.key]: lane.id}
  }

  /**
   * Respondeu "mover" ou "acrescentar": grava e reagrupa.
   *
   * Cancelar não vai ao servidor — basta reagrupar, porque os dados não mudaram e o card volta
   * para as raias que ele de fato tem. É o mesmo mecanismo que o erro usa.
   */
  async onHowToMove(action: MoveAction | '' | null) {
    const asked = this.asking
    this.asking = null
    if (!asked) return
    if (!action) return this.regroup()
    await this.moveToTarget(asked.instance, asked.target, false, asked.origin, action)
  }

  /**
   * Grava a mudança e espelha o resultado no objeto em memória.
   *
   * **Não reagrupa no sucesso**: o vuedraggable já tirou o card de uma lista e pôs na outra, e
   * reagrupar refaria TODAS as listas debaixo do cursor, no meio do gesto do Sortable. O que
   * depende de o bloco ter card é lido a cada desenho (`isEmptyBlock`), então o bloco que
   * esvaziou some sozinho, sem reagrupamento.
   *
   * **Recarrega no erro.** O card já se moveu e não há gesto para desfazer: o quadro não
   * guarda a posição de origem, e mexer nos arrays agora brigaria com o Sortable. Recarregar é
   * também o único caminho correto quando a situação foi gravada e a propriedade não, ou o
   * contrário — são dois endpoints, e um desfazer local teria de adivinhar qual metade valeu.
   */
  private async moveToTarget(
      instance: Instance, target: Coordinate, regroupOnSuccess = false,
      origin: Coordinate = {}, action: MoveAction = 'move'
  ) {
    await this.saveChanges(instance, changesFor(instance, target, this.dimensions, origin, action),
        regroupOnSuccess)
  }

  /**
   * Grava o que já foi decidido, e cuida do que vem DEPOIS de gravar.
   *
   * Mora fora do `moveToTarget` porque nada daqui é sobre arrastar: são as duas regras de
   * reagrupamento, a gravação, o conserto do item em memória, a mensagem e a recusa legível. O
   * arrasto chega com o que o `changesFor` montou de um gesto; a troca rápida, com o conjunto
   * que o usuário marcou à mão. O que acontece em seguida é o mesmo, e escrever de novo deixaria
   * um dos dois para trás na próxima regra que aparecer.
   */
  private async saveChanges(instance: Instance, changes: Plain, regroupOnSuccess = false) {
    if (!Object.keys(changes).length) return
    //REAGRUPA sempre que o card estiver em mais de um lugar, por duas razões distintas:
    //
    //1. O que mudou foi uma dimensão múltipla. Aí o lugar certo não é um só — acrescentar quer o
    //   card de volta na origem, e tirar o vínculo o quer fora de onde foi solto.
    //
    //2. O card JÁ aparecia em vários blocos ou raias, porque alguma dimensão múltipla lhe dá
    //   mais de um valor. O arrasto acertou só a ocorrência tocada: a tarefa que está em S1 e S2
    //   mudava de situação na linha de S1 e continuava na situação antiga na de S2. O dado está
    //   certo nas duas — é o mesmo objeto —, mas a tela não, e é a tela que o usuário lê.
    //
    //Fora disso o quadro NÃO reagrupa, de propósito: refazer as listas debaixo do cursor no meio
    //do gesto briga com o Sortable, e no caso de um valor só o vuedraggable já pôs o card onde
    //ele tem de estar.
    const changedMultiple = this.dimensions.some(d => d.multiple && d.key in changes)
    const inManyPlaces = this.dimensions.some(d => d.multiple && d.valuesOf(instance).length > 1)
    if (changedMultiple || inManyPlaces) regroupOnSuccess = true
    try {
      const service = new EditInstanceService(this.context, this.$instances, this.idea)
      await service.updateChanges(instance._instance, changes)
      applyChangesLocally(instance, changes, this.dimensions)
      this.info(this.movedMessage(changes))
      if (regroupOnSuccess) this.regroup()
      this.$emit('changed')
      await this.recompute(instance)
    } catch (err) {
      //Na janela de recusa, e não na barra (pedido do usuário): o card voltou para onde estava, e
      //a frase precisa ser vista para se entender por quê. Com o "Editar", que abre o item já
      //editando, como o lápis do card: é lá que se preenche o que faltou
      refuse(err, this.idea, {edit: () => this.editRefused(this.contextOf(instance))})
      await this.reload()
    }
  }

  /**
   * O erro do servidor dito na língua da tela, para a barra: a exclusão e a releitura de um card. A
   * recusa de um MOVIMENTO vai para a janela (`refuse`, no `saveChanges`).
   *
   * O quadro grava sem formulário, e a recusa não tem campo onde aparecer: vira a frase do
   * `saveErrorMessage`, a mesma do formulário de edição — as validações de campo e as regras do
   * campo arrastado (pela catraca, só elas: um obrigatório vazio de antes não trava o arrasto por
   * outra dimensão), as do relacionamento reverso, que o quadro grava ao arrastar numa dimensão de
   * espelho, e as do portão.
   *
   * Sem esta tradução chegava como "Erro inesperado": o `handleError` só reconhece
   * `OntosError`, e os erros do servidor são de outra família (`BaseError`). Envolver em
   * `OntosError` é o mesmo que o `throwError` da base faz — e aqui não se pode lançar,
   * porque quem chamou foi o evento de soltar o card.
   *
   * Qualquer outra recusa segue crua: inventar texto para o que não se sabe explicar
   * esconderia o problema em vez de contá-lo.
   */
  private readableError(err: any): any {
    const shared = saveErrorMessage(err, this.idea)
    return shared ? new OntosError(shared, err) : err
  }

  /** "{propriedade} alterado para: {valor}", pela dimensão que de fato mudou. */
  private movedMessage(changes: Plain): string {
    const changed = this.dimensions.filter(dimension => dimension.key in changes)
    return changed.map(dimension => {
      //Numa dimensão múltipla o que foi gravado é a LISTA inteira; o que interessa dizer é o
      //valor de DESTINO, que é o último a entrar nela — os demais já estavam lá.
      const written = changes[dimension.key]
      const id = Array.isArray(written) ? written[written.length - 1] : written
      const value = dimension.values.find(option => option.id === id)
      return this.$tt('info.value-changed', {
        property: dimension.name, value: value ? value.name : this.$tt('no-value')
      })
    }).join(' · ')
  }

  /**
   * Mover pelo atalho do card, e não arrastando.
   *
   * Reagrupa porque aqui NADA saiu do lugar sozinho — no arrasto o vuedraggable já moveu o
   * card, e reagrupar seria trabalho repetido. Lendo o valor que acabou de ser gravado, o card
   * aparece na raia certa; e some, se a raia de destino for terminal e o prazo dela não o
   * alcançar.
   *
   * A trava é contra o toque repetido: são dois cliques no mesmo lugar para andar duas raias,
   * e sem ela o segundo sairia antes de o primeiro ter resposta.
   */
  async moveTo(instance: Instance, lane: LaneOption, from: Coordinate = {},
               dimension: Dimension = this.laneDimension) {
    if (this.movingInstance !== null) return
    const target = {[dimension.key]: lane.id}
    //A mesma pergunta do arrasto, pelo mesmo motivo: um gesto, dois significados. Aqui ela
    //também é uma só, porque o destino foi escolhido no modal.
    if (asksHowToMove(instance, target, this.dimensions)) {
      this.asking = {instance, target, origin: from}
      return
    }
    this.movingInstance = instance._instance
    try {
      await this.moveToTarget(instance, target, true, from)
    } finally {
      this.movingInstance = null
    }
  }

}

export default toNative(KanbanBoard)
</script>

<style scoped>
/* O cabeçalho desenhado em outro lugar (`headerTo`): do tamanho dos botões, e não da largura toda,
   sem a margem de baixo e centrado — ele é parte da linha de lá, e não uma linha própria. Com a
   margem, centrado na linha das abas, os botões dele subiam acima delas (relato do usuário).
   `!important` porque a regra do PageHeader tem o mesmo peso, e ganhar dependeria da ordem em que
   o CSS dos dois chega. */
.board-header--elsewhere {
  width: auto !important;
  margin-bottom: 0 !important;
  align-items: center !important;
}
</style>

